import { useEffect, useMemo, useRef, useState } from "react";
import { X, Loader2, Check, AlertTriangle, Layers, Search, Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useActiveSwing } from "@/hooks/useActiveSwing";
import PhotoUpload from "@/components/PhotoUpload";

/**
 * CustomJobModal — creates a `/custom-submissions` record for ad-hoc work
 * that isn't part of a scheduled inspection template (bucket sheet, GET wear,
 * one-off site form, spot check, etc.).
 *
 * UI rules (from user feedback):
 *   • NO module id field — user shouldn't have to know an internal id.
 *   • Asset picker with typeahead over the whole asset list. If the asset
 *     they need isn't registered, they can type a custom asset number in
 *     the same field and it's accepted verbatim.
 *   • Description is the only required field.
 *   • Photo is optional.
 */
export default function CustomJobModal({ onClose, onCreated }) {
  const { user } = useAuth();
  const { swing: activeSwing } = useActiveSwing(user?.site_code, user);
  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(true);
  const [assetInput, setAssetInput] = useState("");
  const [assetPicked, setAssetPicked] = useState(null); // { id, name, category }
  const [showList, setShowList] = useState(false);
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [workOrder, setWorkOrder] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/assets", { params: { limit: 500 } });
        const list = Array.isArray(data) ? data : (data?.items || data?.results || []);
        setAssets(list.filter((a) => !a.retired));
      } catch { setAssets([]); }
      finally { setLoadingAssets(false); }
    })();
  }, []);

  // Filter the asset dropdown as the user types.
  const filtered = useMemo(() => {
    const q = assetInput.trim().toLowerCase();
    if (!q) return assets.slice(0, 60);
    return assets
      .filter((a) => {
        const hay = [a.id, a.name, a.machine_model, a.asset_type, a.category, a.bucket_number]
          .filter(Boolean).map((x) => String(x).toLowerCase()).join(" ");
        return hay.includes(q);
      })
      .slice(0, 30);
  }, [assets, assetInput]);

  const exactHit = useMemo(() => {
    const q = assetInput.trim().toLowerCase();
    if (!q) return null;
    return assets.find((a) => String(a.id || "").toLowerCase() === q);
  }, [assets, assetInput]);

  const submit = async () => {
    if (!description.trim()) {
      setMsg({ type: "err", text: "Describe what needs doing." });
      return;
    }
    // Resolve final asset id — either the picked asset, an exact-match id
    // typed in the box, or a custom asset number the user free-wrote.
    const finalAssetId = (assetPicked?.id || exactHit?.id || assetInput.trim()) || null;
    if (!finalAssetId) {
      setMsg({ type: "err", text: "Pick or type an asset number." });
      return;
    }

    setBusy(true); setMsg(null);
    try {
      // -------- Primary path: /custom-submissions --------
      // Some sites define an Owner-Center "custom-job" module. If yes, this
      // succeeds and we get full photo support + custom fields. If the site
      // has no such module the server returns "Custom module not found at
      // your site" — we transparently fall back to `/asset-demands` (which
      // every site has by default).
      const values = {
        description: description.trim(),
        notes: notes.trim(),
        asset_id: finalAssetId,
        work_order_number: workOrder.trim() || undefined,
        custom_asset: !!(finalAssetId && !assetPicked && !exactHit),
        photos: photos.map((p) => p.data),
      };
      const opId = `custom-${Date.now()}`;
      try {
        await api.post("/custom-submissions", {
          module_id: "custom-job",
          title: description.trim(),
          work_order_number: workOrder.trim() || undefined,
          values,
          client_op_id: opId,
        });
        setMsg({ type: "ok", text: "Submitted." });
        setTimeout(onCreated, 700);
        return;
      } catch (ex) {
        const detail = String(ex?.response?.data?.detail || ex?.message || "").toLowerCase();
        const shouldFallback = detail.includes("custom module") || detail.includes("not found") || ex?.response?.status === 404;
        if (!shouldFallback) throw ex;
        // -------- Fallback path: /asset-demands (universally available) --
        // AssetDemand.text is capped at 200 chars, so we condense the
        // description + notes + WO# into one line. Photos aren't supported
        // by this endpoint; we surface a soft warning if any were queued.
        const parts = [description.trim()];
        if (workOrder.trim()) parts.push(`[WO: ${workOrder.trim()}]`);
        if (notes.trim()) parts.push(`— ${notes.trim()}`);
        let text = parts.join(" ");
        if (text.length > 200) text = text.slice(0, 197) + "…";
        await api.post("/asset-demands", {
          asset_id: finalAssetId,
          text,
          priority: "normal",
          swing_id: activeSwing?.id || undefined,
        });
        setMsg({
          type: "ok",
          text: photos.length
            ? "Submitted as work request. (Photos aren't attached to work requests — use Breakdown or Planning Request for photo-attached records.)"
            : "Submitted as work request.",
        });
        setTimeout(onCreated, 900);
        return;
      }
    } catch (ex) {
      setBusy(false);
      setMsg({ type: "err", text: apiError(ex) });
    }
  };

  const pickAsset = (a) => {
    setAssetPicked({ id: a.id, name: a.name, category: a.category || a.asset_type });
    setAssetInput(String(a.id));
    setShowList(false);
  };
  const clearPick = () => {
    setAssetPicked(null);
    setAssetInput("");
    setShowList(false);
    inputRef.current?.focus();
  };

  const usingCustomAssetNumber = !!(
    assetInput.trim() && !assetPicked && !exactHit
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="custom-job-modal">
      <div
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
        onClick={onClose}
      />
      <div className="relative panel-heavy w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center gap-3 p-5" style={{ borderBottom: "1px solid var(--border)" }}>
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}
          >
            <Layers className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-muted">Add custom job</div>
            <div className="font-display font-black text-xl tracking-tight">Bucket sheet, GET wear, site form</div>
          </div>
          <button className="btn btn-sm btn-ghost !px-2" onClick={onClose}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {/* Asset picker — combo box */}
          <div className="field">
            <label>Asset</label>
            <div className="relative">
              <div
                className="flex items-center gap-2 px-3 rounded-lg"
                style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
              >
                <Search className="w-4 h-4 text-muted shrink-0" />
                <input
                  ref={inputRef}
                  className="bg-transparent border-none outline-none w-full py-2.5 text-sm"
                  value={assetInput}
                  onChange={(e) => {
                    setAssetInput(e.target.value);
                    setAssetPicked(null);
                    setShowList(true);
                  }}
                  onFocus={() => setShowList(true)}
                  onBlur={() => setTimeout(() => setShowList(false), 160)}
                  placeholder={loadingAssets ? "Loading assets…" : "Pick an asset or type a custom number"}
                  data-testid="custom-asset-input"
                  disabled={loadingAssets}
                />
                {assetInput && (
                  <button
                    onClick={clearPick}
                    className="text-muted hover:text-white shrink-0"
                    title="Clear"
                    data-testid="custom-asset-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {showList && filtered.length > 0 && (
                <div
                  className="absolute z-10 mt-1 w-full panel-glass overflow-hidden"
                  style={{
                    borderRadius: 12,
                    maxHeight: 260,
                    overflowY: "auto",
                    boxShadow: "0 20px 60px -20px rgba(0,0,0,0.9)",
                  }}
                  data-testid="custom-asset-list"
                >
                  {filtered.map((a) => (
                    <button
                      key={a.id || a._id}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pickAsset(a)}
                      className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-[rgba(255,255,255,0.04)] transition-colors"
                      style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}
                      data-testid={`custom-asset-opt-${a.id}`}
                    >
                      <div className="font-mono-tight text-sm font-black" style={{ color: "var(--primary)" }}>{a.id}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{a.name || a.machine_model || a.asset_type}</div>
                        <div className="text-[10px] uppercase tracking-widest text-muted">
                          {(a.category || a.asset_type || "")}
                          {a.bucket_number ? ` · Bucket ${a.bucket_number}` : ""}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {usingCustomAssetNumber && (
              <div
                className="text-[11px] mt-1.5 flex items-center gap-1.5"
                style={{ color: "var(--primary)" }}
                data-testid="custom-asset-freewrite-hint"
              >
                <Plus className="w-3 h-3" />
                Using custom asset number <strong>{assetInput}</strong> — not in the registered list.
              </div>
            )}
            {assetPicked && (
              <div className="text-[11px] mt-1.5 text-muted">
                Picked: <strong className="text-white">{assetPicked.id}</strong>
                {assetPicked.name && <> · {assetPicked.name}</>}
              </div>
            )}
          </div>

          {/* Description — the only mandatory field */}
          <div className="field">
            <label>
              Description <span style={{ color: "var(--primary)" }}>*</span>
            </label>
            <textarea
              className="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What needs doing? e.g. Grease points topped up on Bucket B-04"
              data-testid="custom-description"
            />
          </div>

          {/* Optional Work Order # */}
          <div className="field">
            <label>Work Order # <span className="text-muted text-[10px] uppercase tracking-widest">(optional · skip to add later)</span></label>
            <input
              type="text"
              className="input font-mono-tight"
              value={workOrder}
              onChange={(e) => setWorkOrder(e.target.value)}
              placeholder="e.g. WO-12345"
              maxLength={40}
              data-testid="custom-work-order"
            />
          </div>

          {/* Optional notes */}
          <div className="field">
            <label>Notes <span className="text-muted text-[10px] uppercase tracking-widest">(optional)</span></label>
            <textarea
              className="textarea"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Findings, part numbers, follow-ups…"
              data-testid="custom-notes"
            />
          </div>

          <PhotoUpload
            value={photos}
            onChange={setPhotos}
            max={6}
            label="Photos (optional)"
          />

          {msg && (
            <div className={`pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start`}>
              {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} {msg.text}
            </div>
          )}
        </div>

        <div className="p-4 flex items-center justify-end gap-2" style={{ borderTop: "1px solid var(--border)" }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            style={{ minHeight: 48, fontSize: 15, padding: "0 20px" }}
            onClick={submit}
            disabled={busy || !description.trim()}
            data-testid="custom-submit"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />} Submit
          </button>
        </div>
      </div>
    </div>
  );
}
